import React, { useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Box, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import pallet from '../../../layouts/pallet';
import { User } from '../../../types/userType';
import { dataGridSx } from '../../common/styled/DataGrid';
import tableColumnUtils from '../../../utils/tableColumnUtils';
import { ReportCardDispatch } from '../../../types/reportCardDispatchType';

const STATUS_COLOURS: Record<string, string> = {
  sent: '#1B806A',
  failed: '#B3261E',
  idle: '#6B7280'
};

const StatusText = styled.span<{ $tone: 'sent' | 'failed' | 'idle' }>`
  color: ${({ $tone }) => STATUS_COLOURS[$tone]};
  font-weight: 500;
`;

interface ConsolidatedMarksReportTableProps {
  studentDetails: User[];
  isLoading: boolean;
  onGenerateReportCard: (studentId: string, studentName: string) => void;
  onSendReportCardToParent: (studentId: string) => void;
  isDownloadInProgress: boolean;
  sendingStudentIds: string[];
  generatingStudentIds: string[];
  dispatchByStudentId: Record<string, ReportCardDispatch>;
  canSendToParent: boolean;
}

const ConsolidatedMarksReportTable: React.FC<
  ConsolidatedMarksReportTableProps
> = ({
  studentDetails,
  isLoading,
  onGenerateReportCard,
  onSendReportCardToParent,
  isDownloadInProgress,
  sendingStudentIds,
  generatingStudentIds,
  dispatchByStudentId,
  canSendToParent
}) => {
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'enrollmentNo',
        headerName: 'Enrollment No',
        flex: 1,
        minWidth: 140,
        disableColumnMenu: true
      },
      {
        field: 'name',
        headerName: 'Student Name',
        flex: 1,
        minWidth: 180,
        disableColumnMenu: true
      },
      // Status column and the send button below only exist for schools where the workflow is on.
      ...(canSendToParent
        ? [
      {
        field: 'reportCardStatus',
        headerName: 'Report Card',
        minWidth: 180,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          // No record at all means it was never attempted for this class/year.
          const dispatch = dispatchByStudentId[params.row.id];
          if (!dispatch) {
            return <StatusText $tone='idle'>Not Sent</StatusText>;
          }
          if (dispatch.status === 'FAILED') {
            return <StatusText $tone='failed'>Failed</StatusText>;
          }
          return (
            <StatusText $tone='sent'>
              {dispatch.lastSentAt
                ? `Sent · ${moment(dispatch.lastSentAt).format(
                    'DD MMM, HH:mm'
                  )}`
                : 'Sent'}
            </StatusText>
          );
        }
      } as GridColDef
          ]
        : []),
      {
        field: 'action',
        headerName: 'Action',
        // flex: 1,
        // Widened from 240 to fit the second button alongside "Generate Report Card".
        minWidth: canSendToParent ? 460 : 240,
        sortable: false,
        headerAlign: 'center',
        disableColumnMenu: true,
        renderCell: (params) => {
          const isSendingThisRow = sendingStudentIds.includes(params.row.id);
          const isGeneratingThisRow = generatingStudentIds.includes(
            params.row.id
          );
          const dispatch = dispatchByStudentId[params.row.id];
          // Label reflects what actually happened before, so an admin is never offered a blind
          // "Send" for a student whose parents already have the report card.
          const sendLabel = !dispatch
            ? 'Send to Parent'
            : dispatch.status === 'FAILED'
            ? 'Retry'
            : 'Resend';
          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Fixed widths so a button keeps its size when its label changes
                  ("Generate Report Card" -> "Generating…", "Send to Parent" -> "Sending…"),
                  otherwise the row visibly reflows mid-action. Sized for the longest label. */}
              <Button
                variant='contained'
                onClick={() => onGenerateReportCard(params.row.id,params.row.name)}
                disabled={isDownloadInProgress || isGeneratingThisRow}
                sx={{ minWidth: 210, whiteSpace: 'nowrap' }}
              >
                {isGeneratingThisRow ? 'Generating…' : 'Generate Report Card'}
              </Button>
              {canSendToParent && (
                <Button
                  variant='outlined'
                  onClick={() => onSendReportCardToParent(params.row.id)}
                  disabled={isDownloadInProgress || isSendingThisRow}
                  sx={{ minWidth: 150, whiteSpace: 'nowrap' }}
                >
                  {isSendingThisRow ? 'Sending…' : sendLabel}
                </Button>
              )}
            </Box>
          );
        }
      }
    ],
    [
      onGenerateReportCard,
      onSendReportCardToParent,
      isDownloadInProgress,
      sendingStudentIds,
      generatingStudentIds,
      dispatchByStudentId,
      canSendToParent
    ]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={studentDetails}
        columns={columns}
        loading={isLoading || isDownloadInProgress}
        disableRowSelectionOnClick
        pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
        initialState={tableColumnUtils.DATA_GRID_INITIAL_STATE}
        disableColumnMenu
        sx={{
          ...dataGridSx(pallet),
          '& .MuiDataGrid-virtualScroller': {
            overflowY: 'auto'
          }
        }}
      />
    </Box>
  );
};

export default ConsolidatedMarksReportTable;
